import { useState, useRef } from 'react';
import { 
  Upload, FileText, Brain, Download, Percent, Euro, Loader2, 
  CheckCircle, AlertTriangle, RefreshCw, Trash2, Eye, X,
  Image as ImageIcon, ArrowRight, Sparkles, Calculator, Plus,
  Pencil, Save, ToggleLeft, ToggleRight, User, Building, MapPin, Camera
} from 'lucide-react';
import api from '@/lib/api';
import { toast } from 'sonner';
import { parseFR } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Textarea } from '@/components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { Label } from '@/components/ui/label';
import NumInputFR from '@/components/NumInputFR';

export default function DevisCopier({ onDevisCreated, mode = 'competitor' }) {
  const isLegacy = mode === 'legacy';
  const [step, setStep] = useState(1); // 1: Upload, 2: Analysis, 3: Adjust, 4: Generate
  const [file, setFile] = useState(null);
  const [fileId, setFileId] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [analyzing, setAnalyzing] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [analysis, setAnalysis] = useState(null);
  
  // Editable lines state
  const [editedLines, setEditedLines] = useState([]);
  const [clientInfo, setClientInfo] = useState({ 
    nom: '', 
    adresse: '', 
    telephone: '', 
    email: '',
    type: 'particulier' // 'particulier' or 'professionnel'
  });
  const [devisInfo, setDevisInfo] = useState({ numero: '', date: '', validite: '30 jours' });
  const [projectTitle, setProjectTitle] = useState('');
  
  // Chantier address
  const [chantierAdresse, setChantierAdresse] = useState('');
  const [sameAsClient, setSameAsClient] = useState(true);
  
  // Custom AI instructions
  const [customInstructions, setCustomInstructions] = useState('');
  
  // Before photos (multiple)
  const [beforePhotos, setBeforePhotos] = useState([]);
  const [beforePhotoPreviews, setBeforePhotoPreviews] = useState([]);
  const beforePhotoRef = useRef(null);
  
  // Discount state
  const [discountMode, setDiscountMode] = useState('percent'); // 'percent' or 'euro'
  const [discountValue, setDiscountValue] = useState(0);
  const [globalTvaRate, setGlobalTvaRate] = useState(20);
  
  const fileInputRef = useRef(null);

  const handleFileSelect = (e) => {
    const selectedFile = e.target.files?.[0];
    if (selectedFile) {
      const validTypes = ['application/pdf', 'image/png', 'image/jpeg', 'image/jpg', 'image/webp'];
      if (!validTypes.includes(selectedFile.type)) {
        toast.error('Format non supporté. Utilisez PDF, PNG, JPG ou WEBP.');
        return;
      }
      setFile(selectedFile);
    }
  };

  // Before photo handling
  const handleBeforePhotoSelect = (e) => {
    const files = Array.from(e.target.files || []);
    const validFiles = files.filter(f => f.type.startsWith('image/'));
    
    if (validFiles.length === 0) {
      toast.error('Veuillez sélectionner des images');
      return;
    }
    
    // Limit to 4 photos
    const newPhotos = [...beforePhotos, ...validFiles].slice(0, 4);
    setBeforePhotos(newPhotos);
    
    // Generate previews
    newPhotos.forEach((file, idx) => {
      if (!beforePhotoPreviews[idx]) {
        const reader = new FileReader();
        reader.onload = (e) => {
          setBeforePhotoPreviews(prev => {
            const newPreviews = [...prev];
            newPreviews[idx] = e.target.result;
            return newPreviews;
          });
        };
        reader.readAsDataURL(file);
      }
    });
  };

  const removeBeforePhoto = (index) => {
    setBeforePhotos(prev => prev.filter((_, i) => i !== index));
    setBeforePhotoPreviews(prev => prev.filter((_, i) => i !== index));
  };

  const handleUpload = async () => {
    if (!file) return;
    
    setUploading(true);
    const formData = new FormData();
    formData.append('file', file);
    
    try {
      const response = await api.post('/devis-copier/upload', formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      
      setFileId(response.data.file_id);
      toast.success('Fichier uploadé ! Lancement de l\'analyse...');
      setStep(2);
      
      await handleAnalyze(response.data.file_id);
      
    } catch (error) {
      toast.error('Erreur d\'upload: ' + (error.response?.data?.detail || error.message));
    } finally {
      setUploading(false);
    }
  };

  const handleAnalyze = async (id = fileId) => {
    setAnalyzing(true);
    
    try {
      const response = await api.post(`/devis-copier/analyze/${id}`, {
        custom_instructions: customInstructions
      });
      const analysisData = response.data.analysis;
      setAnalysis(analysisData);
      
      // Initialize editable lines from analysis
      const lines = (analysisData.lignes || []).map((ligne, idx) => ({
        id: idx,
        description: ligne.description || '',
        quantite: ligne.quantite || 1,
        unite: ligne.unite || 'u',
        prix_unitaire: ligne.prix_unitaire || 0,
        tva_rate: analysisData.totaux?.tva_taux || 20
      }));
      setEditedLines(lines);
      
      // Initialize client info from analysis
      setClientInfo({
        nom: analysisData.client?.nom || '',
        adresse: analysisData.client?.adresse || '',
        telephone: analysisData.client?.telephone || '',
        email: analysisData.client?.email || '',
        type: analysisData.client?.type || 'particulier'
      });
      
      // Initialize devis info
      setDevisInfo({
        numero: '', // New number for our devis
        date: new Date().toLocaleDateString('fr-FR'),
        validite: analysisData.devis_info?.validite || '30 jours'
      });
      
      // Initialize project title if present
      setProjectTitle(analysisData.projet?.titre || analysisData.objet || '');
      
      // Initialize chantier address if different
      if (analysisData.chantier?.adresse && analysisData.chantier.adresse !== analysisData.client?.adresse) {
        setChantierAdresse(analysisData.chantier.adresse);
        setSameAsClient(false);
      }
      
      setGlobalTvaRate(analysisData.totaux?.tva_taux || 20);
      
      setStep(3);
      toast.success('Analyse terminée ! Vérifiez et complétez les informations.');
    } catch (error) {
      toast.error('Erreur d\'analyse: ' + (error.response?.data?.detail || error.message));
      setStep(1);
    } finally {
      setAnalyzing(false);
    }
  };

  // Line editing functions
  const updateLine = (index, field, value) => {
    setEditedLines(prev => prev.map((line, idx) => 
      idx === index ? { ...line, [field]: value } : line
    ));
  };

  const addLine = () => {
    setEditedLines(prev => [...prev, {
      id: Date.now(),
      description: 'Nouvelle ligne',
      quantite: 1,
      unite: 'u',
      prix_unitaire: 0,
      tva_rate: globalTvaRate
    }]);
  };

  const removeLine = (index) => {
    setEditedLines(prev => prev.filter((_, idx) => idx !== index));
  };

  // Apply global discount to a price
  const applyDiscount = (price) => {
    if (discountValue <= 0) return price;
    if (discountMode === 'percent') {
      return price * (1 - discountValue / 100);
    } else {
      // Euro mode: distribute discount proportionally
      const totalHT = editedLines.reduce((sum, l) => sum + (l.prix_unitaire * l.quantite), 0);
      if (totalHT <= 0) return price;
      const proportion = (price * 1) / totalHT; // For a single unit
      return Math.max(0, price - (discountValue * proportion));
    }
  };

  // Calculate line total
  const calculateLineTotal = (line) => {
    const discountedPrice = applyDiscount(line.prix_unitaire);
    return discountedPrice * line.quantite;
  };

  // Calculate all totals
  const calculateTotals = () => {
    let totalHT = 0;
    let totalTVA = 0;
    
    editedLines.forEach(line => {
      const lineHT = calculateLineTotal(line);
      const lineTVA = lineHT * (line.tva_rate / 100);
      totalHT += lineHT;
      totalTVA += lineTVA;
    });
    
    return {
      ht: totalHT,
      tva: totalTVA,
      ttc: totalHT + totalTVA
    };
  };

  // Calculate original total (before discount)
  const calculateOriginalTotal = () => {
    return editedLines.reduce((sum, line) => sum + (line.prix_unitaire * line.quantite), 0);
  };

  const handleGeneratePDF = async () => {
    setGenerating(true);
    
    try {
      // Upload before photos if any
      const uploadedPhotoUrls = [];
      for (const photo of beforePhotos) {
        const photoForm = new FormData();
        photoForm.append('file', photo);
        try {
          const uploadRes = await api.post('/devis-generator/upload-line-image', photoForm, {
            headers: { 'Content-Type': 'multipart/form-data' }
          });
          uploadedPhotoUrls.push(uploadRes.data.url);
        } catch (e) {
          console.error('Photo upload failed:', e);
        }
      }
      
      // Prepare the full data for PDF generation
      const pdfData = {
        file_id: fileId,
        client: clientInfo,
        devis_info: {
          ...devisInfo,
          numero: devisInfo.numero || `RR-${Date.now().toString(36).toUpperCase()}`
        },
        project_title: projectTitle,
        chantier_adresse: sameAsClient ? clientInfo.adresse : chantierAdresse,
        before_photo_urls: uploadedPhotoUrls,
        lignes: editedLines.map(line => ({
          description: line.description,
          quantite: line.quantite,
          unite: line.unite,
          prix_unitaire: applyDiscount(line.prix_unitaire),
          tva_rate: line.tva_rate,
          total_ht: calculateLineTotal(line)
        })),
        totaux: calculateTotals(),
        discount_mode: discountMode,
        discount_value: discountValue
      };
      
      const response = await api.post('/devis-copier/generate-pdf-v2', pdfData, {
        responseType: 'blob'
      });
      
      const blob = new Blob([response.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `Devis_${pdfData.devis_info.numero}.pdf`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
      
      toast.success('Devis PDF généré et téléchargé !');
      setStep(4);
      onDevisCreated?.();
      
    } catch (error) {
      toast.error('Erreur de génération: ' + (error.response?.data?.detail || error.message));
    } finally {
      setGenerating(false);
    }
  };

  const resetForm = () => {
    setStep(1);
    setFile(null);
    setFileId(null);
    setAnalysis(null);
    setEditedLines([]);
    setClientInfo({ nom: '', adresse: '', telephone: '', email: '', type: 'particulier' });
    setDevisInfo({ numero: '', date: '', validite: '30 jours' });
    setProjectTitle('');
    setChantierAdresse('');
    setSameAsClient(true);
    setBeforePhotos([]);
    setBeforePhotoPreviews([]);
    setDiscountMode('percent');
    setDiscountValue(0);
    setGlobalTvaRate(20);
  };

  const totals = calculateTotals();
  const originalTotal = calculateOriginalTotal();
  const savingsAmount = originalTotal - totals.ht;

  return (
    <div className="space-y-6" data-testid="devis-copier-container">
      {/* Progress Steps */}
      <div className="flex items-center justify-center gap-2 mb-8">
        {[
          { num: 1, label: 'Upload' },
          { num: 2, label: 'Analyse IA' },
          { num: 3, label: 'Ajustement' },
          { num: 4, label: 'Terminé' }
        ].map((s, idx) => (
          <div key={s.num} className="flex items-center">
            <div className={`w-10 h-10 rounded-full flex items-center justify-center font-bold transition-all ${
              step >= s.num 
                ? 'bg-green-600 text-white' 
                : 'bg-slate-200 text-slate-500'
            }`}>
              {step > s.num ? <CheckCircle className="w-5 h-5" /> : s.num}
            </div>
            <span className={`ml-2 text-sm ${step >= s.num ? 'text-slate-900 font-medium' : 'text-slate-400'}`}>
              {s.label}
            </span>
            {idx < 3 && <ArrowRight className="w-4 h-4 text-slate-300 mx-3" />}
          </div>
        ))}
      </div>

      {/* Step 1: Upload */}
      {step === 1 && (
        <div className="bg-white rounded-xl p-8 border-2 border-dashed border-slate-300 text-center">
          <input
            type="file"
            ref={fileInputRef}
            onChange={handleFileSelect}
            accept=".pdf,.png,.jpg,.jpeg,.webp"
            className="hidden"
            data-testid="devis-file-input"
          />
          
          {!file ? (
            <div 
              className="cursor-pointer py-12 hover:bg-slate-50 rounded-lg transition-colors"
              onClick={() => fileInputRef.current?.click()}
              data-testid="devis-upload-zone"
            >
              <Upload className="w-16 h-16 text-slate-400 mx-auto mb-4" />
              <h3 className="text-xl font-semibold text-slate-900 mb-2">
                {isLegacy ? 'Uploadez votre ancien devis' : 'Uploadez le devis concurrent'}
              </h3>
              <p className="text-slate-500 mb-4">
                PDF ou image (PNG, JPG, WEBP){isLegacy && ' — devis fait avant l\'utilisation du CRM'}
              </p>
              <Button variant="outline" data-testid="devis-choose-file-btn">
                <Upload className="w-4 h-4 mr-2" />
                Choisir un fichier
              </Button>
            </div>
          ) : (
            <div className="py-6">
              <div className="flex items-center justify-center gap-4 mb-4">
                {file.type === 'application/pdf' ? (
                  <FileText className="w-12 h-12 text-red-500" />
                ) : (
                  <ImageIcon className="w-12 h-12 text-blue-500" />
                )}
                <div className="text-left">
                  <p className="font-medium text-slate-900">{file.name}</p>
                  <p className="text-sm text-slate-500">
                    {(file.size / 1024 / 1024).toFixed(2)} MB
                  </p>
                </div>
                <Button variant="ghost" size="sm" onClick={() => setFile(null)}>
                  <Trash2 className="w-4 h-4 text-red-500" />
                </Button>
              </div>
              
              {/* Custom Instructions Zone */}
              <div className="bg-gradient-to-r from-purple-50 to-indigo-50 rounded-xl p-4 mb-4 border border-purple-200">
                <div className="flex items-center gap-2 mb-2">
                  <Sparkles className="w-4 h-4 text-purple-600" />
                  <label className="font-medium text-purple-900 text-sm">
                    Instructions pour l'IA (optionnel)
                  </label>
                </div>
                <Textarea
                  value={customInstructions}
                  onChange={(e) => setCustomInstructions(e.target.value)}
                  placeholder="Ex: Réduis les prix de 15%, change le nom du client en 'M. Dupont', ajoute une ligne pour la main d'oeuvre à 500€..."
                  className="bg-white/80 border-purple-200 focus:border-purple-400 min-h-[80px] text-sm"
                  data-testid="custom-instructions-input"
                />
                <p className="text-xs text-purple-600 mt-1">
                  L'IA appliquera ces modifications pendant l'analyse
                </p>
              </div>
              
              <Button 
                onClick={handleUpload} 
                disabled={uploading}
                className="bg-green-600 hover:bg-green-700"
                data-testid="devis-analyze-btn"
              >
                {uploading ? (
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                ) : (
                  <Brain className="w-4 h-4 mr-2" />
                )}
                {uploading ? 'Upload en cours...' : 'Analyser avec l\'IA'}
              </Button>
            </div>
          )}
        </div>
      )}

      {/* Step 2: Analyzing */}
      {step === 2 && analyzing && (
        <div className="bg-gradient-to-br from-purple-600 to-indigo-700 rounded-xl p-12 text-center text-white">
          <div className="relative">
            <Brain className="w-20 h-20 mx-auto mb-6 animate-pulse" />
            <Sparkles className="w-8 h-8 absolute top-0 right-1/3 animate-bounce text-yellow-300" />
          </div>
          <h3 className="text-2xl font-bold mb-2">Analyse IA en cours...</h3>
          <p className="text-purple-200 mb-4">
            Extraction des lignes, prix et informations du devis
          </p>
          <div className="w-64 h-2 bg-white/20 rounded-full mx-auto overflow-hidden">
            <div className="h-full bg-white/60 rounded-full animate-pulse" style={{ width: '60%' }}></div>
          </div>
        </div>
      )}

      {/* Step 3: Adjust prices and lines */}
      {step === 3 && analysis && (
        <div className="space-y-6">
          {/* Concurrent info — masqué en mode legacy (c'est NOTRE ancien devis) */}
          {!isLegacy && analysis.concurrent?.nom && (
            <div className="bg-amber-50 rounded-lg p-4 border border-amber-200">
              <p className="text-sm text-amber-700">
                <strong>Devis concurrent :</strong> {analysis.concurrent.nom}
              </p>
            </div>
          )}

          {/* Project title */}
          <div className="bg-white rounded-xl p-6 border border-slate-200">
            <h4 className="font-semibold text-slate-900 mb-4 flex items-center gap-2">
              <FileText className="w-4 h-4 text-[#0157b8]" />
              Objet du devis
            </h4>
            <Input
              value={projectTitle}
              onChange={(e) => setProjectTitle(e.target.value)}
              placeholder="Ex: Rénovation salle de bain, Isolation combles..."
              data-testid="project-title-input"
            />
          </div>

          {/* Client info - Enhanced */}
          <div className="bg-white rounded-xl p-6 border border-slate-200">
            <h4 className="font-semibold text-slate-900 mb-4 flex items-center gap-2">
              <User className="w-4 h-4 text-[#0157b8]" />
              Informations client
            </h4>
            
            {/* Client type */}
            <div className="mb-4">
              <Label className="text-sm font-medium mb-2 block">Type de client</Label>
              <RadioGroup 
                value={clientInfo.type} 
                onValueChange={(v) => setClientInfo(prev => ({ ...prev, type: v }))}
                className="flex gap-4"
              >
                <div className="flex items-center space-x-2">
                  <RadioGroupItem value="particulier" id="particulier" />
                  <Label htmlFor="particulier" className="flex items-center gap-1 cursor-pointer">
                    <User className="w-4 h-4" /> Particulier
                  </Label>
                </div>
                <div className="flex items-center space-x-2">
                  <RadioGroupItem value="professionnel" id="professionnel" />
                  <Label htmlFor="professionnel" className="flex items-center gap-1 cursor-pointer">
                    <Building className="w-4 h-4" /> Professionnel
                  </Label>
                </div>
              </RadioGroup>
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <Label className="text-sm">Nom {clientInfo.type === 'professionnel' ? '/ Société' : ''}</Label>
                <Input
                  value={clientInfo.nom}
                  onChange={(e) => setClientInfo(prev => ({ ...prev, nom: e.target.value }))}
                  placeholder={clientInfo.type === 'professionnel' ? "Nom de l'entreprise" : "Nom du client"}
                  data-testid="client-name-input"
                  className="mt-1"
                />
              </div>
              <div>
                <Label className="text-sm">Téléphone</Label>
                <Input
                  value={clientInfo.telephone}
                  onChange={(e) => setClientInfo(prev => ({ ...prev, telephone: e.target.value }))}
                  placeholder="06 00 00 00 00"
                  className="mt-1"
                />
              </div>
              <div>
                <Label className="text-sm">Adresse</Label>
                <Input
                  value={clientInfo.adresse}
                  onChange={(e) => setClientInfo(prev => ({ ...prev, adresse: e.target.value }))}
                  placeholder="Adresse complète"
                  className="mt-1"
                />
              </div>
              <div>
                <Label className="text-sm">Email</Label>
                <Input
                  value={clientInfo.email}
                  onChange={(e) => setClientInfo(prev => ({ ...prev, email: e.target.value }))}
                  placeholder="email@exemple.com"
                  className="mt-1"
                />
              </div>
            </div>
          </div>

          {/* Chantier address */}
          <div className="bg-white rounded-xl p-6 border border-slate-200">
            <h4 className="font-semibold text-slate-900 mb-4 flex items-center gap-2">
              <MapPin className="w-4 h-4 text-[#0157b8]" />
              Adresse du chantier
            </h4>
            
            <div className="mb-3">
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={sameAsClient}
                  onChange={(e) => setSameAsClient(e.target.checked)}
                  className="rounded border-slate-300"
                />
                <span className="text-sm">Même adresse que le client</span>
              </label>
            </div>
            
            {!sameAsClient && (
              <Input
                value={chantierAdresse}
                onChange={(e) => setChantierAdresse(e.target.value)}
                placeholder="Adresse du chantier si différente"
              />
            )}
          </div>

          {/* Before photos */}
          <div className="bg-white rounded-xl p-6 border border-slate-200">
            <h4 className="font-semibold text-slate-900 mb-4 flex items-center gap-2">
              <Camera className="w-4 h-4 text-[#0157b8]" />
              Photos avant travaux (optionnel, max 4)
            </h4>
            
            <input
              type="file"
              accept="image/*"
              multiple
              ref={beforePhotoRef}
              onChange={handleBeforePhotoSelect}
              className="hidden"
            />
            
            <div className="flex flex-wrap gap-3">
              {beforePhotoPreviews.map((preview, idx) => (
                <div key={idx} className="relative">
                  <img src={preview} alt={`Avant ${idx + 1}`} className="w-24 h-24 object-cover rounded-lg" />
                  <button
                    onClick={() => removeBeforePhoto(idx)}
                    className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full p-1"
                  >
                    <X className="w-3 h-3" />
                  </button>
                </div>
              ))}
              
              {beforePhotos.length < 4 && (
                <button
                  onClick={() => beforePhotoRef.current?.click()}
                  className="w-24 h-24 border-2 border-dashed border-slate-300 rounded-lg flex flex-col items-center justify-center text-slate-400 hover:border-[#0157b8] hover:text-[#0157b8] transition-colors"
                >
                  <Camera className="w-6 h-6" />
                  <span className="text-xs mt-1">Ajouter</span>
                </button>
              )}
            </div>
          </div>

          {/* Discount selector - € or % */}
          <div className="bg-white rounded-xl p-6 border border-slate-200">
            <h4 className="font-semibold text-slate-900 mb-4 flex items-center gap-2">
              {discountMode === 'percent' ? (
                <Percent className="w-5 h-5 text-green-600" />
              ) : (
                <Euro className="w-5 h-5 text-green-600" />
              )}
              Réduction globale
            </h4>
            
            {/* Mode toggle */}
            <div className="flex items-center gap-4 mb-4 p-3 bg-slate-50 rounded-lg">
              <span className="text-sm font-medium text-slate-700">Mode :</span>
              <div className="flex gap-2">
                <Button
                  variant={discountMode === 'percent' ? 'default' : 'outline'}
                  size="sm"
                  onClick={() => { setDiscountMode('percent'); setDiscountValue(0); }}
                  className={discountMode === 'percent' ? 'bg-[#0157b8]' : ''}
                  data-testid="discount-mode-percent"
                >
                  <Percent className="w-4 h-4 mr-1" />
                  Pourcentage
                </Button>
                <Button
                  variant={discountMode === 'euro' ? 'default' : 'outline'}
                  size="sm"
                  onClick={() => { setDiscountMode('euro'); setDiscountValue(0); }}
                  className={discountMode === 'euro' ? 'bg-[#0157b8]' : ''}
                  data-testid="discount-mode-euro"
                >
                  <Euro className="w-4 h-4 mr-1" />
                  Euros
                </Button>
              </div>
            </div>

            {/* Discount value */}
            <div className="flex flex-wrap items-center gap-4">
              {discountMode === 'percent' ? (
                <>
                  <div className="flex gap-2">
                    {[0, 5, 10, 15, 20].map(d => (
                      <Button
                        key={d}
                        variant={discountValue === d ? "default" : "outline"}
                        size="sm"
                        onClick={() => setDiscountValue(d)}
                        className={discountValue === d ? "bg-green-600 hover:bg-green-700" : ""}
                      >
                        {d === 0 ? 'Aucune' : `-${d}%`}
                      </Button>
                    ))}
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="text-sm text-slate-500">Personnalisé:</span>
                    <NumInputFR
                      value={discountValue}
                      onChange={(n) => setDiscountValue(n)}
                      className="w-20"
                      max={100}
                      data-testid="discount-percent-input"
                    />
                    <span className="text-sm text-slate-500">%</span>
                  </div>
                </>
              ) : (
                <>
                  <div className="flex gap-2">
                    {[0, 50, 100, 200, 500].map(d => (
                      <Button
                        key={d}
                        variant={discountValue === d ? "default" : "outline"}
                        size="sm"
                        onClick={() => setDiscountValue(d)}
                        className={discountValue === d ? "bg-green-600 hover:bg-green-700" : ""}
                      >
                        {d === 0 ? 'Aucune' : `-${d}€`}
                      </Button>
                    ))}
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="text-sm text-slate-500">Personnalisé:</span>
                    <NumInputFR
                      value={discountValue}
                      onChange={(n) => setDiscountValue(n)}
                      className="w-24"
                      data-testid="discount-euro-input"
                    />
                    <span className="text-sm text-slate-500">€</span>
                  </div>
                </>
              )}
              
              {discountValue > 0 && (
                <Badge className="bg-green-100 text-green-700">
                  Économie: {savingsAmount.toFixed(2)} € HT
                </Badge>
              )}
            </div>
          </div>

          {/* Editable Lines table */}
          <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
            <div className="p-4 bg-slate-50 border-b border-slate-200 flex justify-between items-center">
              <h4 className="font-semibold text-slate-900 flex items-center gap-2">
                <Calculator className="w-5 h-5 text-[#0157b8]" />
                Lignes du devis ({editedLines.length})
              </h4>
              <Button size="sm" variant="outline" onClick={addLine} data-testid="add-line-btn">
                <Plus className="w-4 h-4 mr-1" />
                Ajouter ligne
              </Button>
            </div>
            
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-slate-100">
                  <tr>
                    <th className="text-left px-3 py-3 text-xs font-semibold text-slate-600 uppercase w-1/3">Description</th>
                    <th className="text-center px-2 py-3 text-xs font-semibold text-slate-600 uppercase w-16">Qté</th>
                    <th className="text-center px-2 py-3 text-xs font-semibold text-slate-600 uppercase w-16">Unité</th>
                    <th className="text-center px-2 py-3 text-xs font-semibold text-slate-600 uppercase w-24">P.U. HT</th>
                    <th className="text-center px-2 py-3 text-xs font-semibold text-slate-600 uppercase w-20">TVA %</th>
                    <th className="text-right px-3 py-3 text-xs font-semibold text-slate-600 uppercase w-24">Total HT</th>
                    <th className="text-center px-2 py-3 text-xs font-semibold text-slate-600 uppercase w-12"></th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {editedLines.map((line, idx) => {
                    const lineTotal = calculateLineTotal(line);
                    const hasDiscount = discountValue > 0;
                    const discountedPU = applyDiscount(line.prix_unitaire);
                    
                    return (
                      <tr key={line.id} className="hover:bg-slate-50" data-testid={`devis-line-${idx}`}>
                        <td className="px-3 py-2">
                          <Textarea
                            value={line.description}
                            onChange={(e) => updateLine(idx, 'description', e.target.value)}
                            className="text-sm min-h-[40px] resize-none"
                            rows={1}
                          />
                        </td>
                        <td className="px-2 py-2">
                          <NumInputFR
                            value={line.quantite}
                            onChange={(n) => updateLine(idx, 'quantite', (n || 1))}
                            className="w-16 text-center text-sm h-9"
                          />
                        </td>
                        <td className="px-2 py-2">
                          <Select value={line.unite} onValueChange={(v) => updateLine(idx, 'unite', v)}>
                            <SelectTrigger className="w-16 h-9 text-sm">
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="u">u</SelectItem>
                              <SelectItem value="m²">m²</SelectItem>
                              <SelectItem value="ml">ml</SelectItem>
                              <SelectItem value="m³">m³</SelectItem>
                              <SelectItem value="h">h</SelectItem>
                              <SelectItem value="j">j</SelectItem>
                              <SelectItem value="forfait">forfait</SelectItem>
                              <SelectItem value="kg">kg</SelectItem>
                              <SelectItem value="L">L</SelectItem>
                            </SelectContent>
                          </Select>
                        </td>
                        <td className="px-2 py-2">
                          <div className="flex flex-col items-center">
                            <NumInputFR
                              value={line.prix_unitaire}
                              onChange={(n) => updateLine(idx, 'prix_unitaire', n)}
                              className="w-24 text-right text-sm h-9"
                            />
                            {hasDiscount && (
                              <span className="text-xs text-green-600 mt-1">
                                → {discountedPU.toFixed(2)} €
                              </span>
                            )}
                          </div>
                        </td>
                        <td className="px-2 py-2">
                          <Select value={line.tva_rate.toString()} onValueChange={(v) => updateLine(idx, 'tva_rate', parseFloat(v))}>
                            <SelectTrigger className="w-20 h-9 text-sm">
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="0">0%</SelectItem>
                              <SelectItem value="5.5">5.5%</SelectItem>
                              <SelectItem value="10">10%</SelectItem>
                              <SelectItem value="20">20%</SelectItem>
                            </SelectContent>
                          </Select>
                        </td>
                        <td className="px-3 py-2 text-right">
                          <span className="font-medium text-slate-900">
                            {lineTotal.toFixed(2)} €
                          </span>
                        </td>
                        <td className="px-2 py-2 text-center">
                          <Button 
                            variant="ghost" 
                            size="sm" 
                            onClick={() => removeLine(idx)}
                            className="text-red-500 hover:text-red-700 hover:bg-red-50"
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>

            {editedLines.length === 0 && (
              <div className="p-8 text-center text-slate-500">
                Aucune ligne. Cliquez sur "Ajouter ligne" pour commencer.
              </div>
            )}

            {/* Totals */}
            <div className="p-4 bg-slate-50 border-t border-slate-200">
              <div className="flex justify-end">
                <div className="w-72 space-y-2">
                  <div className="flex justify-between text-sm">
                    <span className="text-slate-600">Total HT</span>
                    <span className="font-medium">{totals.ht.toFixed(2)} €</span>
                  </div>
                  {discountValue > 0 && (
                    <div className="flex justify-between text-sm text-green-600">
                      <span>Réduction appliquée</span>
                      <span>-{savingsAmount.toFixed(2)} €</span>
                    </div>
                  )}
                  <div className="flex justify-between text-sm">
                    <span className="text-slate-600">TVA</span>
                    <span className="font-medium">{totals.tva.toFixed(2)} €</span>
                  </div>
                  <div className="flex justify-between text-lg font-bold border-t border-slate-300 pt-2">
                    <span>Total TTC</span>
                    <span className="text-green-600">{totals.ttc.toFixed(2)} €</span>
                  </div>
                  {analysis?.totaux?.total_ttc && discountValue > 0 && (
                    <div className="text-xs text-slate-500 text-right">
                      {isLegacy ? 'Devis original' : 'Original concurrent'}: {analysis.totaux.total_ttc.toFixed(2)} € TTC
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>

          {/* Actions */}
          <div className="flex justify-between items-center">
            <Button variant="outline" onClick={resetForm}>
              <RefreshCw className="w-4 h-4 mr-2" />
              Recommencer
            </Button>
            <Button 
              onClick={handleGeneratePDF}
              disabled={generating || editedLines.length === 0}
              className="bg-green-600 hover:bg-green-700"
              size="lg"
              data-testid="generate-pdf-btn"
            >
              {generating ? (
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              ) : (
                <Download className="w-4 h-4 mr-2" />
              )}
              {generating ? 'Génération...' : 'Générer le PDF'}
            </Button>
          </div>
        </div>
      )}

      {/* Step 4: Success */}
      {step === 4 && (
        <div className="bg-gradient-to-br from-green-500 to-emerald-600 rounded-xl p-12 text-center text-white">
          <CheckCircle className="w-20 h-20 mx-auto mb-6" />
          <h3 className="text-2xl font-bold mb-2">Devis généré avec succès !</h3>
          <p className="text-green-100 mb-6">
            Le PDF a été téléchargé. Vous pouvez l'envoyer à votre client.
          </p>
          <div className="flex justify-center gap-4">
            <Button 
              variant="outline" 
              onClick={resetForm}
              className="bg-white text-green-600 hover:bg-green-50"
            >
              <RefreshCw className="w-4 h-4 mr-2" />
              Copier un autre devis
            </Button>
            <Button 
              variant="outline"
              onClick={() => window.location.href = '/devis'}
              className="bg-white/10 text-white border-white/30 hover:bg-white/20"
            >
              Voir mes devis
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}
